import { useLocation } from "react-router-dom";
import {
  useLazyGetExportStopQuery,
  useLazyGetExportSummaryQuery,
  useLazyGetExportVehicleRoutesQuery,
  useLazyGetExportVehicleTripsQuery,
} from "../Redux/service/NewReports/ExportReport";
import {
  useLazyGetAttendanceReportFileQuery,
  useLazyGetDailyAttendanceReportFileQuery,
  useLazyGetDurationFileQuery,
  useLazyGetProductivityFileQuery,
} from "../Redux/service/NewReports/Workforce";
import { useLazyGeServiceRequestFileQuery } from "../Redux/service/NewReports/ServicesRequestReports";
import { useLazyGetShelfAvailabilityExportQuery } from "../Redux/service/NewReports/ShelfAvailability";
import { useLazyGetProductExpirationExportQuery } from "../Redux/service/NewReports/ProductExpiration";

export const useExportHandlers = () => {
  const [exportRoutes] = useLazyGetExportVehicleRoutesQuery();
  const [exportTrips] = useLazyGetExportVehicleTripsQuery();
  const [exportStop] = useLazyGetExportStopQuery();
  const [exportSummary] = useLazyGetExportSummaryQuery();
  const [exportDurationEfficency] = useLazyGetDurationFileQuery();
  const [exportProductivity] = useLazyGetProductivityFileQuery();
  const [exportRequests] = useLazyGeServiceRequestFileQuery();
  const [exportDailyAttendance] = useLazyGetDailyAttendanceReportFileQuery();
  const [exportAttendance] = useLazyGetAttendanceReportFileQuery();
  const [exportShelfAvailability] = useLazyGetShelfAvailabilityExportQuery();
  const [exportProductExpiration] = useLazyGetProductExpirationExportQuery();
  const { pathname } = useLocation();
  const shorturl = pathname.replace(/\/+$/, "").split("/").pop();
  console.log("shorturl", shorturl);
  async function getReportBlob({
    shorturl,
    startDate,
    endDate,
    format = "excel",
  }) {
    if (shorturl === "efficiency") {
      return await exportDurationEfficency({
        val: format,
        startDate,
        endDate,
      }).unwrap();
    } else if (shorturl === "productivity") {
      return await exportProductivity({
        val: format,
        startDate,
        endDate,
      }).unwrap();
    } else if (shorturl === "requests") {
      return await exportRequests({ val: format, startDate, endDate }).unwrap();
    } else if (shorturl === "dailyAttendance") {
      return await exportDailyAttendance({
        val: format,
        startDate,
        endDate,
      }).unwrap();
    } else if (shorturl === "attendance") {
      return await exportAttendance({
        val: format,
        startDate,
        endDate,
      }).unwrap();
    } else {
      throw new Error(`Unknown report type: ${shorturl}`);
    }
  }
  function getReportFilename(shorturl, format = "xlsx") {
    if (shorturl === "efficiency") {
      return `durationEfficiency.${format}`;
    } else if (shorturl === "productivity") {
      return `productivity.${format}`;
    } else if (shorturl === "requests") {
      return `requests.${format}`;
    } else if (shorturl === "dailyAttendance") {
      return `Attendance_Report.${format}`;
    } else if (shorturl === "attendance") {
      return `Shift_Comp.${format}`;
    } else {
      return `report.${format}`; // fallback
    }
  }

  const handleExportReport = async ({
    fileName,
    exportIds,
    startDate,
    endDate,
    filters = {},
  }) => {
    if (
      shorturl !== "efficiency" &&
      shorturl !== "productivity" &&
      shorturl !== "requests" &&
      shorturl !== "dailyAttendance" &&
      shorturl !== "attendance" &&
      shorturl !== "shelfAvailability"
    ) {
      if (!fileName || !exportIds || !startDate || !endDate) {
        console.warn("Missing export parameters");
        return;
      }
    }

    const params = {
      from: startDate,
      to: endDate,
      selectedids: exportIds,
    };

    try {
      if (shorturl === "productExpiration") {
        const norm = (v) => {
          if (v === null || v === undefined) return undefined;
          if (Array.isArray(v)) return v.length ? v.join(",") : undefined;
          if (typeof v === "string" && v.trim() === "") return undefined;
          return v;
        };
        const blob = await exportProductExpiration({
          from:             norm(startDate),
          to:               norm(endDate),
          criticalDays:     norm(filters.criticalDays),
          warningDays:      norm(filters.warningDays),
          noticeDays:       norm(filters.noticeDays),
          statuses:         filters.tableStatuses?.length
                              ? filters.tableStatuses.join(",")
                              : norm(filters.expiryStatus),
          skuIds:           norm(filters.tableSkuIds),
          poiIds:           norm(filters.tablePoiIds),
          recordedByIds:    norm(filters.tableRecordedByIds),
          categoryIds:      norm(filters.tableCategories),
          poiTypes:         norm(filters.tablePoiTypes),
          trackingContexts: norm(filters.tableItemPlaces),
        }).unwrap();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const dateTag = new Date().toISOString().split("T")[0];
        link.setAttribute("download", `Product_Expiration_Report_${dateTag}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        return;
      }

      if (shorturl === "shelfAvailability") {
        const norm = (v) => {
          if (v === null || v === undefined) return undefined;
          if (Array.isArray(v)) return v.length ? v.join(",") : undefined;
          if (typeof v === "string" && v.trim() === "") return undefined;
          return v;
        };
        // Mirror exactly the params the report query sends (WorkforceReport.jsx)
// In exportReportHandler.js, inside the shelfAvailability block
// Read Sort from context or pass it through filters
const blob = await exportShelfAvailability({
  formId: process.env.REACT_APP_SHELF_FORM_ID,
  fromDate: norm(startDate),
  toDate: norm(endDate),
  storeId:     norm(filters.storeId),
  sku:         norm(filters.sku),
  repId:       norm(filters.repId),
  states:      norm(filters.states),
  storeTypes:  norm(filters.tableStoreTypes),
  categoryIds: norm(filters.tableCategories),
}).unwrap();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "ShelfAvailability.xlsx");
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        return;
      }

      if (
        shorturl !== "efficiency" &&
        shorturl !== "productivity" &&
        shorturl !== "requests" &&
        shorturl !== "dailyAttendance" &&
        shorturl !== "attendance"
      ) {
        switch (fileName) {
          case "Routes Report": {
            const blob = await exportRoutes(params).unwrap();
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "Routes_Report.xlsx");
            document.body.appendChild(link);
            link.click();
            link.remove();
            break;
          }
          case "Trips Report": {
            const blob = await exportTrips(params).unwrap();
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "Trips_Report.xlsx");
            document.body.appendChild(link);
            link.click();
            link.remove();
            break;
          }
          case "Stop Report": {
            const blob = await exportStop(params).unwrap();
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "Stop_Report.xlsx");
            document.body.appendChild(link);
            link.click();
            link.remove();
            break;
          }
          case "Summary Report": {
            const blob = await exportSummary(params).unwrap();
            const url = window.URL.createObjectURL(new Blob([blob]));
            const timestamp = new Date()
              .toISOString()
              .replace(/[:.]/g, "-")
              .replace("T", "_")
              .split("Z")[0];
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `Report ${timestamp}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            break;
          }
          default:
            console.warn(`No export logic defined for file: ${fileName}`);
            break;
        }
      } else {
        const blob = await getReportBlob({
          shorturl,
          startDate,
          endDate,
          format: "excel",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", getReportFilename(shorturl, "xlsx"));

        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error("Export failed", err);
    }
  };

  return { handleExportReport };
};
